import { useMemo, useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Check,
  CalendarDays,
  Calendar as CalendarIcon,
  ListChecks,
  Layers,
} from 'lucide-react';
import { DesktopMonthCalendar } from './DesktopMonthCalendar';
import {
  STATUS_CONFIG,
  toMinutes,
  getAulaStatusParaData,
} from '../../components/chamada';
import type { AulaStatus } from '../../components/chamada';
import type { HorarioAula, Turma } from '../../types/models';
import { useHojeReferencia, useSimClock } from '../../api/hooks/useSimClock';
import './chamada-professor-desktop.css';
import './professor-dashboard-desktop.css';

function formatDateSafe(dateStr: string, format?: Intl.DateTimeFormatOptions): string {
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 12, 0, 0);
      if (!isNaN(date.getTime())) return date.toLocaleDateString('pt-BR', format);
    }
  } catch { /* ignore */ }
  return dateStr;
}

export function formatTurmaDisplay(turma?: Turma | { nome?: string; serie?: string }): string {
  if (!turma) return '';
  const nome = (turma.nome || '').trim();
  const serie = (turma.serie || '').trim();

  // Caso 1: "2ª Série B" ou "6º Ano A" -> "2ª SÉRIE · TURMA B"
  const match = nome.match(/^(.+?)\s+([A-Za-z0-9])$/);
  if (match) {
    const s = match[1].toUpperCase();
    const letra = match[2].toUpperCase();
    return `${s} · TURMA ${letra}`;
  }

  // Caso 2: "1° Ano - M" -> "1° ANO · TURMA M"
  const matchHifen = nome.match(/^(.+?)\s*-\s*([A-Za-z0-9])$/);
  if (matchHifen) {
    const s = matchHifen[1].toUpperCase();
    const letra = matchHifen[2].toUpperCase();
    return `${s} · TURMA ${letra}`;
  }

  if (serie && nome && !nome.toUpperCase().includes('TURMA')) {
    return `${serie.toUpperCase()} · TURMA ${nome.toUpperCase()}`;
  }

  return nome.toUpperCase();
}

export interface ChamadaProfessorDesktopProps {
  user: { id: string; nome?: string };
  dataAtual: string;
  setDataAtual?: (d: string) => void;
  diaSemana: string;
  aulasHoje: HorarioAula[];
  chamadasDia: Array<{
    id: string;
    horarioAulaId?: string;
    turmaId: string;
    materiaId?: string;
    numeroAula?: number | null;
    status: string;
    totalPresentes: number;
    totalAusentes: number;
    totalAlunos?: number;
  }>;
  statusMap: Record<string, { status: string; totalPresentes: number; totalAusentes: number; totalAlunos?: number }>;
  turmas: Turma[];
  horarioAtualId?: string;
  aulaAgora: HorarioAula | null;
  chamadaId: string | null;
  loadingAlunos?: boolean;
  /** True enquanto prepara RFID/status/alunos — não troca de fase ainda. */
  iniciandoChamada?: boolean;
  contagemPorTurma?: Record<string, number>;
  todosAlunos?: unknown[];
  iniciarChamada: (aula?: HorarioAula) => Promise<void>;
  openAulaDetail: (slot: HorarioAula) => void;
  setDialogPerdida: (h: HorarioAula) => void;
  setFaseAtual: (f: 'briefing' | 'operacao' | 'resumo') => void;
}

export function ChamadaProfessorDesktop({
  user,
  dataAtual,
  setDataAtual,
  aulasHoje,
  chamadasDia,
  statusMap,
  turmas,
  horarioAtualId,
  aulaAgora,
  chamadaId,
  loadingAlunos,
  iniciandoChamada = false,
  contagemPorTurma,
  todosAlunos = [],
  iniciarChamada,
  openAulaDetail,
  setDialogPerdida,
  setFaseAtual,
}: ChamadaProfessorDesktopProps) {
  const { hojeStr, isSimulation } = useHojeReferencia();
  const { virtualEpochMs } = useSimClock({
    enabled: isSimulation,
    pollMs: isSimulation ? 2_000 : 15_000,
  });
  const [horaAtual, setHoraAtual] = useState(() => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`;
  });

  useEffect(() => {
    const tick = () => {
      const n =
        isSimulation && Number.isFinite(virtualEpochMs) && virtualEpochMs > 0
          ? new Date(virtualEpochMs)
          : new Date();
      setHoraAtual(
        `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`,
      );
    };
    tick();
    if (isSimulation) return; // virtualEpochMs já atualiza via poll/postMessage
    const t = setInterval(tick, 30_000);
    return () => clearInterval(t);
  }, [isSimulation, virtualEpochMs]);

  const hojeRef = hojeStr || dataAtual;
  const isHoje = !!hojeRef && dataAtual === hojeRef;
  const dataLabelRaw = formatDateSafe(dataAtual, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const dataLabel = dataLabelRaw ? dataLabelRaw.charAt(0).toUpperCase() + dataLabelRaw.slice(1) : '';

  const statusMapSimple = useMemo(() => {
    const m: Record<string, string> = {};
    chamadasDia.forEach((c) => {
      const key = c.horarioAulaId || `${c.turmaId}:${c.materiaId ?? ''}`;
      m[key] = c.status;
      if (c.numeroAula != null && c.numeroAula > 0) {
        m[`n:${c.turmaId}:${c.numeroAula}`] = c.status;
      }
    });
    return m;
  }, [chamadasDia]);

  const countAlunosTurma = (turmaId: string) => contagemPorTurma?.[turmaId] ?? 0;

  const resolveStatus = (slot: HorarioAula): AulaStatus => {
    const info =
      statusMap[slot.id]
      ?? statusMap[`${slot.turmaId}:${slot.materiaId ?? ''}`]
      ?? (slot.numeroAula != null ? statusMap[`n:${slot.turmaId}:${slot.numeroAula}`] : undefined);
    return getAulaStatusParaData(slot, {
      dataReferencia: dataAtual,
      chamadaInfo: info,
      horarioAtualId: isHoje ? horarioAtualId : undefined,
      horaAtual,
      qtdAlunos: countAlunosTurma(slot.turmaId),
      loadingAlunos: !!loadingAlunos,
    });
  };

  const kpis = useMemo(() => {
    let feitas = 0;
    let perdidas = 0;
    let pendentes = 0;
    let total = 0;
    aulasHoje.forEach((a) => {
      const st = resolveStatus(a);
      if (st === 'vazia') return;
      total++;
      if (st === 'feita') feitas++;
      else if (st === 'perdida') perdidas++;
      else pendentes++;
    });
    return { total, feitas, perdidas, pendentes };
  }, [aulasHoje, dataAtual, statusMap, horaAtual, loadingAlunos, contagemPorTurma, isHoje, horarioAtualId]);

  const [dayStatsCache, setDayStatsCache] = useState<Record<string, { feitas: number; pendentes: number; total: number }>>({});

  useEffect(() => {
    if (!dataAtual) return;
    if (aulasHoje.length === 0 && loadingAlunos) return;
    setDayStatsCache((prev) => {
      const current = prev[dataAtual];
      if (
        current &&
        current.feitas === kpis.feitas &&
        current.pendentes === (kpis.pendentes + kpis.perdidas) &&
        current.total === kpis.total
      ) {
        return prev;
      }
      return {
        ...prev,
        [dataAtual]: {
          feitas: kpis.feitas,
          pendentes: kpis.pendentes + kpis.perdidas,
          total: kpis.total,
        },
      };
    });
  }, [dataAtual, kpis, aulasHoje.length, loadingAlunos]);

  const pctConcluido = kpis.total > 0 ? Math.round((kpis.feitas / kpis.total) * 100) : 0;

  const handleClickAula = (h: HorarioAula) => {
    if (iniciandoChamada) return;
    const status = resolveStatus(h);
    const isVazia = status === 'vazia';
    const isActive = isHoje && aulaAgora?.id === h.id;
    const aulaJaFeita = status === 'feita';

    if (isVazia) return;
    if (status === 'perdida') {
      setDialogPerdida(h);
      return;
    }
    if (status === 'feita') {
      openAulaDetail(h);
      return;
    }
    if (isActive && !aulaJaFeita) {
      if (chamadaId) setFaseAtual('operacao');
      else void iniciarChamada(h);
      return;
    }
    if (status === 'proxima' || status === 'agora') {
      if (!isHoje) {
        toast.info('Chamada só pode ser feita no dia da aula.');
        return;
      }
      const minAtual = toMinutes(horaAtual);
      const minInicio = h.horaInicio ? toMinutes(h.horaInicio) : 999;
      const faltam = minInicio - minAtual;
      if (status === 'proxima' && faltam > 5) {
        toast.info(`Aguarde. A aula inicia às ${h.horaInicio} (faltam ${faltam} min).`);
        return;
      }
      void iniciarChamada(h);
    }
  };

  const dayPanelDateRaw = formatDateSafe(dataAtual, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const dayPanelDate = dayPanelDateRaw ? dayPanelDateRaw.toUpperCase() : '';

  const turnosAgrupados = useMemo(() => {
    const grupos: {
      key: string;
      label: string;
      horaInicio: string;
      horaFim: string;
      aulas: HorarioAula[];
    }[] = [];

    const ordenadas = [...aulasHoje].sort((a, b) =>
      (a.horaInicio || '').localeCompare(b.horaInicio || '')
    );

    ordenadas.forEach((aula) => {
      const turma = turmas.find((t) => t.id === aula.turmaId);
      let tKey = turma?.turno || '';
      let tLabel = '';
      if (tKey === 'MANHA') tLabel = 'Manhã';
      else if (tKey === 'TARDE') tLabel = 'Tarde';
      else if (tKey === 'NOITE') tLabel = 'Noite';
      else if (tKey === 'INTEGRAL') tLabel = 'Integral';
      else {
        const h = parseInt((aula.horaInicio || '').split(':')[0], 10);
        if (h < 12) {
          tKey = 'MANHA';
          tLabel = 'Manhã';
        } else if (h < 18) {
          tKey = 'TARDE';
          tLabel = 'Tarde';
        } else {
          tKey = 'NOITE';
          tLabel = 'Noite';
        }
      }

      let g = grupos.find((x) => x.key === tKey);
      if (!g) {
        g = {
          key: tKey,
          label: tLabel,
          horaInicio: aula.horaInicio || '',
          horaFim: aula.horaFim || '',
          aulas: [],
        };
        grupos.push(g);
      }
      g.aulas.push(aula);
      if (aula.horaInicio && (!g.horaInicio || aula.horaInicio < g.horaInicio)) g.horaInicio = aula.horaInicio;
      if (aula.horaFim && (!g.horaFim || aula.horaFim > g.horaFim)) g.horaFim = aula.horaFim;
    });

    return grupos;
  }, [aulasHoje, turmas]);

  const [turnosColapsadosManual, setTurnosColapsadosManual] = useState<Record<string, boolean>>({});

  const toggleTurno = (turnoKey: string, autoCollapsed: boolean) => {
    setTurnosColapsadosManual((prev) => {
      const current = prev[turnoKey] !== undefined ? prev[turnoKey] : autoCollapsed;
      return { ...prev, [turnoKey]: !current };
    });
  };

  const dayPanel = (
    <div className="cham-desk-day-panel">
      <header className="cham-desk-day-head">
        <div className="cham-desk-day-head-main">
          <p className="cham-desk-day-title-inline">
            <span className="cham-desk-day-kicker">MOVIMENTO DO DIA</span>
            <span className="cham-desk-day-sep">—</span>
            <span className="cham-desk-day-date">{dayPanelDate}</span>
          </p>
          {iniciandoChamada && (
            <p className="cham-desk-day-meta" style={{ color: '#2563eb', textTransform: 'uppercase' }}>
              PREPARANDO CHAMADA…
            </p>
          )}
        </div>
      </header>

      <div className="cham-desk-day-kpis-grid">
        <div className="dash-desk-kpi cham-desk-kpi--compact">
          <div className="dash-desk-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
            <Layers size={12} />
          </div>
          <div>
            <div className="dash-desk-kpi-val">{kpis.total}</div>
            <div className="dash-desk-kpi-lbl">Aulas</div>
          </div>
        </div>
        <div className="dash-desk-kpi cham-desk-kpi--compact">
          <div className="dash-desk-kpi-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
            <CheckCircle2 size={12} />
          </div>
          <div>
            <div className="dash-desk-kpi-val">{kpis.feitas}</div>
            <div className="dash-desk-kpi-lbl">Feitas</div>
          </div>
        </div>
        <div className="dash-desk-kpi cham-desk-kpi--compact">
          <div className="dash-desk-kpi-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
            <Clock size={12} />
          </div>
          <div>
            <div className="dash-desk-kpi-val">{kpis.pendentes}</div>
            <div className="dash-desk-kpi-lbl">Pendentes</div>
          </div>
        </div>
        <div className="dash-desk-kpi cham-desk-kpi--compact">
          <div
            className="dash-desk-kpi-icon"
            style={{
              background: kpis.perdidas ? '#fef3c7' : '#f8fafc',
              color: kpis.perdidas ? '#d97706' : '#94a3b8',
            }}
          >
            <AlertTriangle size={12} />
          </div>
          <div>
            <div className="dash-desk-kpi-val">{kpis.perdidas}</div>
            <div className="dash-desk-kpi-lbl">Fora Hor.</div>
          </div>
        </div>
      </div>

      {kpis.total > 0 && (
        <div className="cham-desk-day-progress" aria-hidden>
          <div className="cham-desk-day-progress-track">
            <div className="cham-desk-day-progress-bar" style={{ width: `${pctConcluido}%` }} />
          </div>
          <span>{pctConcluido}% concluído</span>
        </div>
      )}

      {aulasHoje.length === 0 ? (
        <p className="cham-desk-day-empty">Sem aulas na grade neste dia da semana.</p>
      ) : (
        <div className="cham-desk-mov-list">
          {turnosAgrupados.map((grupo) => {
            const totalAulasGrupo = grupo.aulas.length;
            const feitasGrupo = grupo.aulas.filter((h) => resolveStatus(h) === 'feita').length;
            const todasFeitas = totalAulasGrupo > 0 && feitasGrupo === totalAulasGrupo;
            const isCollapsed = turnosColapsadosManual[grupo.key] !== undefined
              ? turnosColapsadosManual[grupo.key]
              : todasFeitas;

            return (
              <div key={grupo.key} className={`cham-desk-turno-section ${isCollapsed ? 'cham-desk-turno-section--collapsed' : ''}`}>
                <button
                  type="button"
                  className={`cham-desk-turno-header ${todasFeitas ? 'cham-desk-turno-header--done' : ''}`}
                  onClick={() => toggleTurno(grupo.key, todasFeitas)}
                  title={isCollapsed ? 'Clique para expandir o turno' : 'Clique para recolher o turno'}
                  aria-expanded={!isCollapsed}
                >
                  <span className="cham-desk-turno-time">{grupo.horaInicio}</span>
                  <div className="cham-desk-turno-title-wrap">
                    <span className="cham-desk-turno-title">TURNO {grupo.label.toUpperCase()}</span>
                    {todasFeitas && (
                      <span className="cham-desk-turno-done-badge">
                        <Check size={10} strokeWidth={2.5} /> {feitasGrupo}/{totalAulasGrupo} CONCLUÍDAS
                      </span>
                    )}
                  </div>
                  <div className="cham-desk-turno-right">
                    <span className="cham-desk-turno-time">{grupo.horaFim}</span>
                    {isCollapsed ? (
                      <ChevronDown size={14} className="cham-desk-turno-icon" />
                    ) : (
                      <ChevronUp size={14} className="cham-desk-turno-icon" />
                    )}
                  </div>
                </button>

                {!isCollapsed && (
                  <ul className="cham-desk-turno-aulas">
                {grupo.aulas.map((h) => {
                  const status = resolveStatus(h);
                  const cfg = STATUS_CONFIG[status];
                  const turma = turmas.find((t) => t.id === h.turmaId);
                  const stats = statusMap[h.id];
                  const qtd = countAlunosTurma(h.turmaId);
                  const isVazia = status === 'vazia';
                  const isActive = isHoje && aulaAgora?.id === h.id && status !== 'feita';
                  const clickable =
                    !isVazia &&
                    (status === 'feita' ||
                      status === 'perdida' ||
                      isActive ||
                      status === 'agora' ||
                      (status === 'proxima' && isHoje));

                  const statusClass =
                    status === 'feita' ? 'ok' : status === 'perdida' ? 'risk' : 'pend';

                  return (
                    <li key={h.id} className="cham-desk-mov-li">
                      <button
                        type="button"
                        className={[
                          'cham-desk-mov-item',
                          `cham-desk-mov-item--${status}`,
                          isActive ? 'cham-desk-mov-item--active' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => handleClickAula(h)}
                        disabled={!clickable}
                      >
                        <div className={`cham-desk-mov-top cham-desk-mov-top--${statusClass} cham-desk-mov-top--${status}`}>
                          <div className="cham-desk-mov-title-group">
                            <span className="cham-desk-mov-title-text">
                              {(h.materia?.nome || `${h.numeroAula}ª Aula`).toUpperCase()}
                            </span>
                            {turma && (
                              <>
                                <span className="cham-desk-mov-sep">·</span>
                                <span className="cham-desk-mov-title-text">{formatTurmaDisplay(turma)}</span>
                              </>
                            )}
                            {h.coberturado && (
                              <span className="cham-desk-mov-cobertura">
                                COBERTURA
                              </span>
                            )}
                          </div>
                          <span className={`cham-desk-mov-badge cham-desk-mov-badge--${statusClass}`}>
                            {cfg.label}
                          </span>
                        </div>

                        <div className="cham-desk-mov-bottom">
                          <span className="cham-desk-mov-time-start">
                            {h.horaInicio || '--:--'}
                          </span>

                          <div className="cham-desk-mov-center-info">
                            <span className="cham-desk-mov-duracao">
                              {(() => {
                                if (!h.horaInicio || !h.horaFim) return '50 MINUTOS';
                                const [h1, m1] = h.horaInicio.split(':').map(Number);
                                const [h2, m2] = h.horaFim.split(':').map(Number);
                                const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
                                return `${diff > 0 ? diff : 50} MINUTOS`;
                              })()}
                            </span>
                            {!loadingAlunos && !isVazia && (
                              <>
                                <span className="cham-desk-mov-sep">—</span>
                                <span className="cham-desk-mov-alunos">
                                  {qtd} {qtd === 1 ? 'ALUNO' : 'ALUNOS'}
                                </span>
                              </>
                            )}
                            {status === 'feita' && stats && (
                              <>
                                <span className="cham-desk-mov-sep">—</span>
                                <span className="cham-desk-mov-stats-wrap">
                                  <span className="cham-desk-mov-stat-p">{stats.totalPresentes}P</span>
                                  <span className="cham-desk-mov-stat-dot">·</span>
                                  <span className="cham-desk-mov-stat-f">{stats.totalAusentes}F</span>
                                </span>
                              </>
                            )}
                          </div>

                          <div className="cham-desk-mov-end-group">
                            <span className="cham-desk-mov-time-end">
                              {h.horaFim || '--:--'}
                            </span>
                            {clickable && (
                              <ChevronRight size={13} className="cham-desk-mov-chevron" aria-hidden />
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-page text-[13px] leading-snug dash-desk-page cham-desk-page">
      <div className="dash-desk-root cham-desk-root">
        <div className="cham-desk-cal-wrap">
          <DesktopMonthCalendar
            professorId={user.id}
            statusMap={statusMapSimple}
            selectedIso={dataAtual}
            onSelectIso={setDataAtual}
            hojeIso={hojeRef}
            pickerOnly
            dayPanel={dayPanel}
            calendarTitle={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.90rem', fontWeight: 700, color: '#0f172a' }}>
                  {dataLabel}
                </span>
                {isHoje && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '1px 6px',
                      borderRadius: 4,
                      background: '#eff6ff',
                      color: '#2563eb',
                      border: '1px solid #bfdbfe',
                      fontSize: '0.60rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                    }}
                  >
                    HOJE
                  </span>
                )}
              </div>
            }
            calendarSubtitle=""
            hideLegend={true}
            onGoHoje={() => setDataAtual && setDataAtual(hojeRef)}
            countUnit={{ one: 'chamada', many: 'chamadas' }}
            chamadasSummaryByDay={dayStatsCache}
          />
        </div>
      </div>
    </div>
  );
}
